import React, { ReactNode, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import AccordionDetails from '@mui/material/AccordionDetails'
import styled from '@emotion/styled'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import VerifiedIcon from '@mui/icons-material/Verified'
import ErrorIcon from '@mui/icons-material/Error'
import { IssueEntity } from 'types/IssueEntity'
import InvestorsFields, { InvestorForm, InvestorFormWithId } from '../../shared/investors/AdminInvestorsFields'
import FeeStructureFields from '../../shared/AdminFeeStructureFields'
import { formatDateYYYYMMDD, formatPercentage } from '../../../../packages/utils/numberFormatters'
import { InvestorsFieldsSanityCheckProps } from '../../shared/investors/AdminInvestorFieldsSanityCheck'
import { ShouldShow, ShouldShowAny } from '../../../../components/entities/IssueType'
import IpoFields from '../AdminIpoFields'
import AdvisorDetails from '../AdminAdvisorDetails'
import LinkedIssuesDetails from '../linked-issues/AdminLinkedIssuesDetails'
import ImportantDates from '../AdminImportantDates'
import TermsFields from '../AdminTermsFields'
import IssueIsinDetails from '../AdminIssueIsinDetails'
import { CommentsFields } from '../../shared/AdminCommentsFields'
import OutcomeFields from '../AdminOutcomeFields'
import { SourcesFields } from '../../shared/source-fields'
import { WarrantDetails } from './WarrantsDetails'
import IssuesGroupApi from '../../../../api/IssuesGroupApi'
import Grid from '@mui/material/Grid'
import {
    checkAdvisors,
    checkFeeStructureValues,
    checkImportantDatesValues,
    checkInvestorsValues,
    checkOutcomeValues,
    checkTermsValues,
} from '../../../../components/utils/calculateIssueStatus'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { FeeFormWithId } from '../../types/FeeForm'

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
    ({ theme, sx = {} }) => ({
        border: `1px solid ${theme.palette.divider}`,
        '&:not(:last-child)': {
            borderBottom: sx && 'marginBottom' in sx ? undefined : 0,
        },
        '&::before': {
            display: 'none',
        },
    }),
)

const issueHasId = (issue: FormIssueEntity): issue is FormIssueEntity & { id: string } => !!issue.id

const investorsValuesSanityChecker = (issue: FormIssueEntity) => (investors: InvestorFormWithId[]) => {
    const calcTotals = (investors: InvestorFormWithId[]) => {
        const toF = (str: string | number | null) => (str ? parseFloat(str as string) : 0)
        const cashValue = (p: InvestorFormWithId, issue: FormIssueEntity) =>
            p.amountInCash
                ? toF(p.amountInCash)
                : toF(p.amountInPercentage) * toF(issue.offeredUnits) * toF(issue.unitSubPrice)
        const sums = investors.reduce(
            (acc, p) => {
                acc.cash += toF(p.amountInCash) || 0
                acc.percentage += toF(p.amountInPercentage) || 0
                if (parseEventualInt(p.feeLevel ?? 0) === 0) acc.level0 += cashValue(p, issue)
                if (parseEventualInt(p.feeLevel ?? 0) > 0) acc.level1 += cashValue(p, issue)
                return acc
            },
            { cash: 0, percentage: 0, level0: 0, level1: 0 },
        )
        return {
            cash:
                sums.cash +
                sums.percentage *
                    0.01 *
                    parseEventualInt(issue.offeredUnits ?? 0) *
                    parseEventualFloat(issue.unitSubPrice ?? 0),
            percentage:
                sums.percentage +
                (sums.cash /
                    (parseEventualInt(issue.offeredUnits ?? 0) * parseEventualFloat(issue.unitSubPrice ?? 0))) *
                    100,
            level0: sums.level0,
            level1: sums.level1,
        }
    }
    const totals = calcTotals(investors)

    const calcWarnings = (totals: InvestorsFieldsSanityCheckProps['totals'], issue: FormIssueEntity) => {
        let warningOfferedUnits = false
        let totalOfferingCash = null
        if (issue.offeredUnits && parseEventualFloat(issue.offeredUnits) > 0) {
            totalOfferingCash = parseEventualFloat(issue.offeredUnits) * parseEventualFloat(issue.unitSubPrice ?? 0)
            warningOfferedUnits = (totals.cash - totalOfferingCash) / totalOfferingCash > 0.01
        }

        return {
            warningOfferedUnits,
            totalOfferingCash,
        }
    }
    const warnings = calcWarnings(totals, issue)

    return {
        totals,
        warnings,
    } as InvestorsFieldsSanityCheckProps
}

const isIpoOrRightsIssue = (issue: Pick<IssueEntity, 'type'>) => ['Rights issue', 'IPO'].includes(issue.type)

const validateInvestorsLeo = (issue: Pick<IssueEntity, 'type'>) => (_value: string, values: InvestorForm) => {
    if (values.isSeller) return true
    if (isIpoOrRightsIssue(issue)) return !values.leo

    return true
}

const validateInvestorsAmount = () => (_value: string, values: InvestorForm) => {
    if (values.isSeller) return true
    if (_value === '0') return false
    if (values.amountInCash && values.amountInPercentage) return false

    return true
}
function FormAccordionSummary({
    children,
    description,
    missingFields,
}: {
    children: ReactNode
    description?: ReactNode
    missingFields?: string[]
}) {
    return (
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Grid container sx={{ width: '100%' }}>
                <Grid size={2}>
                    <Typography variant='h6'>{children}</Typography>
                </Grid>
                <Grid size={4}>
                    <div>
                        <Typography marginLeft={8} sx={{ display: 'flex' }}>
                            {missingFields?.length === 0 ? (
                                <VerifiedIcon color='success' />
                            ) : (
                                <ErrorIcon
                                    color='error'
                                    sx={{ visibility: missingFields === undefined ? 'hidden' : 'visible' }}
                                />
                            )}{' '}
                            <span style={{ paddingLeft: 8 }}>{description}</span>
                            {!!missingFields?.length && `Saknade fält: ${missingFields.join(', ')}`}
                        </Typography>
                    </div>
                </Grid>
            </Grid>
        </AccordionSummary>
    )
}

const issuesGroupApi = new IssuesGroupApi()

const parseEventualInt = (i: number | string) => {
    if (typeof i === 'string') return parseInt(i)

    return i
}

const parseEventualFloat = (i: number | string) => {
    if (typeof i === 'string') return parseFloat(i)

    return i
}

export function FormAccordion({
    voteDisableSave,
    setIsCustomFormDirty,
}: {
    setIsCustomFormDirty: () => void
    voteDisableSave: (f: string, v: boolean) => void
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const formMethods = useFormContext<FormIssueEntity>()
    const [issueGroupsCount, setIssueGroupsCount] = useState<number | null>(null)
    const issue = formMethods.watch()

    useEffect(() => {
        const fetchIssueGroups = async () => {
            if (!issue.companyId) return
            const issueGroups = await issuesGroupApi.findByCompanyId(issue.companyId)

            setIssueGroupsCount(issueGroups.filter((ig) => ig.issueId === issue.id).length)
        }

        void fetchIssueGroups()
    }, [issue.companyId, issue.id])

    const totalOfferedUnits =
        parseEventualInt(issue.offeredUnits || 0) + parseEventualInt(issue.offeredUnitsUnlisted ?? 0)

    const calculateSubQuota = () => {
        const unitSubTotal = parseEventualInt(issue.unitSubTotal || 0)
        const abbTarget = parseEventualInt(issue.abbTarget || 0)
        const unitSubPrice = parseEventualFloat(issue.unitSubPrice || 0)
        const isAbb = issue.type === 'ABB Directed issue' && abbTarget > 0
        const subQuota = isAbb ? (unitSubTotal / abbTarget) * unitSubPrice : unitSubTotal / totalOfferedUnits
        return subQuota
    }
    const calculateUnitSubTotal = () => {
        const subUnitTotal = parseEventualInt(issue.unitSubTotal || 0)
        const unitSubWithRights = parseEventualInt(issue.unitSubWithRights || 0)
        const unitSubWithoutRights = parseEventualInt(issue.unitSubWithoutRights || 0)
        const unitSubGuarantor = parseEventualInt(issue.unitSubGuarantor || 0)
        const unitPctSubWithRights = parseEventualFloat(issue.unitPctSubWithRights || 0)
        const unitPctSubWithoutRights = parseEventualFloat(issue.unitPctSubWithoutRights || 0)
        const unitPctSubGuarantor = parseEventualFloat(issue.unitPctSubGuarantor || 0)
        const total =
            unitSubWithRights +
            unitSubWithoutRights +
            unitSubGuarantor +
            (totalOfferedUnits * (unitPctSubWithRights + unitPctSubWithoutRights + unitPctSubGuarantor)) / 100
        const withinRange = Math.abs((1.0 * subUnitTotal - 1.0 * total) / total) < 0.01
        return { unitSubTotal: total, withinRange }
    }
    const calculatedSubQuota = calculateSubQuota()

    const formattedSubQuota = (
        <span style={calculatedSubQuota > 1 ? { color: 'red', fontWeight: 'bold' } : {}}>
            {formatPercentage(calculatedSubQuota * 100, { decimals: 1, locale })}
        </span>
    )

    const { unitSubTotal: calculatedUnitSubTotal, withinRange: calcUnitSubTotalWithinRange } = calculateUnitSubTotal()

    // flags to be used in more than one segment
    const noOverAllotmentChecked = !!issue.noOverAllotment

    const noSecondaryOfferingChecked = !!issue.noSecondaryOffering
    const noGuarantorsChecked = !!issue.noGuarantors
    const noInvestorsNamedChecked = !!issue.noInvestorsNamed

    const publicationSource = issue.sources.find((s) => s.sourceType === 'Publication') || null
    const defaultAllExpanded = issue?.type === 'Guarantor fee issue'
    const investorsValuesSanityCheckerMemo = React.useMemo(() => investorsValuesSanityChecker(issue), [issue])

    const latestSource = issue.sources.sort(
        (a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime(),
    )[0]

    return (
        <React.Fragment>
            <ShouldShow issueType={issue?.type} issueField='ipoData'>
                {issue.ipoData && !issue.ipoData.instrumentMillistreamInsref ? (
                    <Accordion defaultExpanded={defaultAllExpanded}>
                        <FormAccordionSummary>Bolag och aktie</FormAccordionSummary>
                        <AccordionDetails>
                            <IpoFields issue={issue} />
                        </AccordionDetails>
                    </Accordion>
                ) : null}
            </ShouldShow>

            <ShouldShow issueType={issue?.type} issueField='commentsFields'>
                <Accordion defaultExpanded={defaultAllExpanded}>
                    <FormAccordionSummary
                        description={issue.statusHideUntil && `Snooze: ${formatDateYYYYMMDD(issue.statusHideUntil)}`}
                    >
                        Kommentarer
                    </FormAccordionSummary>
                    <AccordionDetails>
                        <CommentsFields setIsCustomFormDirty={setIsCustomFormDirty} />
                    </AccordionDetails>
                </Accordion>
            </ShouldShow>
            <Accordion defaultExpanded={defaultAllExpanded} sx={{ marginBottom: 6 }}>
                <FormAccordionSummary
                    description={
                        latestSource && `${formatDateYYYYMMDD(latestSource.publishedDate)} ${latestSource.sourceType}`
                    }
                >
                    Källor
                </FormAccordionSummary>
                <AccordionDetails>
                    <SourcesFields isIssue setIsCustomFormDirty={setIsCustomFormDirty} />
                </AccordionDetails>
            </Accordion>

            <ShouldShow issueType={issue?.type} issueField='advisorFields'>
                <Accordion defaultExpanded={defaultAllExpanded}>
                    <FormAccordionSummary missingFields={checkAdvisors(issue)}>Rådgivare</FormAccordionSummary>
                    <AccordionDetails>
                        <AdvisorDetails entity={issue} entityType='issue' setIsCustomFormDirty={setIsCustomFormDirty} />
                    </AccordionDetails>
                </Accordion>
            </ShouldShow>
            <ShouldShowAny
                issueType={issue?.type}
                issueFields='recordDate, incRightsDate, exRightsDate, rightsStartDate, rightsEndDate, subStartDate, subEndDate, generalMeetingDate, ipoTradingDate'
            >
                <Accordion defaultExpanded={defaultAllExpanded}>
                    <FormAccordionSummary missingFields={checkImportantDatesValues(issue)}>Datum</FormAccordionSummary>
                    <AccordionDetails>
                        <ImportantDates
                            issue={issue}
                            publicationSource={publicationSource}
                            voteDisableSave={voteDisableSave}
                        />
                    </AccordionDetails>
                </Accordion>
            </ShouldShowAny>
            <ShouldShowAny
                issueType={issue?.type}
                issueFields='sharesRequired, rightsReceived, rightsRequired, unitsReceived, sharesInUnit, unitSubPrice, offeredUnits, abbTarget, secondaryOffering'
            >
                <Accordion defaultExpanded={defaultAllExpanded} sx={{ marginBottom: 6 }}>
                    <FormAccordionSummary missingFields={checkTermsValues(issue)}>Villkor</FormAccordionSummary>
                    <AccordionDetails>
                        <TermsFields
                            issue={issue}
                            noSecondaryOfferingChecked={noSecondaryOfferingChecked}
                            setIsCustomFormDirty={setIsCustomFormDirty}
                        />
                    </AccordionDetails>
                </Accordion>
            </ShouldShowAny>

            <ShouldShow issueType={issue?.type} issueField='feeStructureFields'>
                <Accordion defaultExpanded={defaultAllExpanded}>
                    <FormAccordionSummary missingFields={checkFeeStructureValues(issue)}>
                        Garantiarvoden
                    </FormAccordionSummary>
                    <AccordionDetails>
                        <FeeStructureFields
                            noGuarantorsChecked={noGuarantorsChecked}
                            setIsCustomFormDirty={setIsCustomFormDirty}
                        />
                    </AccordionDetails>
                </Accordion>
            </ShouldShow>
            <ShouldShow issueType={issue?.type} issueField='investorsFields'>
                <Accordion defaultExpanded={defaultAllExpanded} sx={{ marginBottom: 6 }}>
                    <FormAccordionSummary missingFields={checkInvestorsValues(issue)}>Investerare</FormAccordionSummary>
                    <AccordionDetails>
                        <InvestorsFields
                            issue={issue}
                            entityType='issue'
                            acceptedFeeLevels={
                                issue.fees
                                    .filter((fee): fee is FeeFormWithId & { feeLevel: string } => fee.feeLevel !== null)
                                    .map(({ feeLevel }) => +feeLevel) || []
                            }
                            currency={issue.currency}
                            isFeeLevelEnabled={isIpoOrRightsIssue(issue)}
                            isLeoEnabled={!isIpoOrRightsIssue(issue)}
                            isWithoutRightsEnabled={isIpoOrRightsIssue(issue)}
                            sanityChecker={investorsValuesSanityCheckerMemo}
                            noInvestorsNamedChecked={noInvestorsNamedChecked}
                            setIsCustomFormDirty={setIsCustomFormDirty}
                            validateAmount={validateInvestorsAmount()}
                            validateLeo={validateInvestorsLeo(issue)}
                        />
                    </AccordionDetails>
                </Accordion>
            </ShouldShow>

            <Accordion defaultExpanded={defaultAllExpanded}>
                <FormAccordionSummary missingFields={checkOutcomeValues(issue)}>Utfall</FormAccordionSummary>
                <AccordionDetails>
                    <OutcomeFields
                        issue={issue}
                        noOverAllotmentChecked={noOverAllotmentChecked}
                        noSecondaryOfferingChecked={noSecondaryOfferingChecked}
                        noGuarantorsChecked={noGuarantorsChecked}
                        subscriptionQuota={formattedSubQuota}
                        calculatedUnitSubTotal={calculatedUnitSubTotal}
                        calcUnitSubTotalWithinRange={calcUnitSubTotalWithinRange}
                    />
                </AccordionDetails>
            </Accordion>

            <ShouldShow issueType={issue?.type} issueField='linkedIssues'>
                <Accordion defaultExpanded={defaultAllExpanded}>
                    <FormAccordionSummary>Länkade emissioner ({issueGroupsCount})</FormAccordionSummary>
                    <AccordionDetails>
                        {issueHasId(issue) && (
                            <LinkedIssuesDetails
                                entity={issue}
                                entityType='issue'
                                setIsCustomFormDirty={setIsCustomFormDirty}
                            />
                        )}
                        {!issueHasId(issue) && 'Emissionen måste sparas innan länkgrupper kan skapas.'}
                    </AccordionDetails>
                </Accordion>
            </ShouldShow>
            {!!issue?.warrants?.length && (
                <Accordion defaultExpanded>
                    <FormAccordionSummary>Teckningsoptioner</FormAccordionSummary>
                    <AccordionDetails>
                        <WarrantDetails issue={issue} />
                    </AccordionDetails>
                </Accordion>
            )}
            <ShouldShow issueType={issue?.type} issueField='rightsIssueDetails'>
                <Accordion defaultExpanded={defaultAllExpanded} sx={{ marginTop: 6 }}>
                    <FormAccordionSummary>ISIN</FormAccordionSummary>
                    <AccordionDetails>
                        <IssueIsinDetails issue={issue} setIsCustomFormDirty={setIsCustomFormDirty} />
                    </AccordionDetails>
                </Accordion>
            </ShouldShow>
        </React.Fragment>
    )
}
